import React, { FC, useEffect, useState, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { InformationCircleIcon } from '@heroicons/react/solid'
import ROUTES from '../../utils/routes'
import { StyledSubscriptionSelectPage, SubscriptionCard, UserIcons } from '..'
import { PageLayout, ActionDialog } from '../../System'
import {
  TRIAL,
  MONTH,
  YEAR,
  FOREVER,
  monthPrice,
  annualPrice,
  trialPrice,
  foreverPrice
} from '../../utils/stripePrices'
import { userStore } from '../../utils/UserContext'
import useAccountSubscription from '../useAccountSubscription'
import { CloseButton } from '../../System/CloseButton'
import { useNotifications } from '../../Notifications'
import { SplitContext } from '../../utils/SplitContext'
import usePricing from '../../../redux/pricing/usePricing'

const SubscriptionSelectPage: FC = () => {
  const {
    subscriptionInfo,
    periodEndsInDays,
    loadingSubscription,
    actions: { loadSubscriptionInfo, cancelUserSubscription }
  } = useAccountSubscription()
  const { user } = useContext(userStore)
  const { addNotification } = useNotifications()
  const history = useHistory()

  const [cardsOpen, setCardsOpen] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const showTrialOption = !user?.hasTrialed && user?.stripeSubscriptionStatus === 'Inactive'
  const showUserIcons = showTrialOption || subscriptionInfo?.plan?.interval === TRIAL

  // Get price data
  const { actions: { getPriceData } } = usePricing()
  const monthPriceData = getPriceData('subscription', monthPrice)
  const annualPriceData = getPriceData('subscription', annualPrice)
  const trialPriceData = getPriceData('subscription', trialPrice)
  const foreverPriceData = getPriceData('subscription', foreverPrice)

  const annualSavings = 89 + (annualPriceData?.sale?.value || 0)

  const {
    isReady,
    isEnabled,
    treatments: { trialOption }
  } = useContext(SplitContext)

  const handleCancelUserSub = async () => {
    try {
      await cancelUserSubscription()

      setShowModal(false)
      addNotification({
        message: 'You have successfully unsubscribed.',
        type: 'success',
        interval: 5000
      })

      history.push(ROUTES.user.settings.billing)
    }
    catch (err) {
      addNotification({
        message: err.message,
        type: 'error',
        interval: 5000
      })
    }
  }

  useEffect(() => {
    loadSubscriptionInfo()
  }, [])

  return (
    <PageLayout maxWidth={showTrialOption ? 1200 : 800}>
      <StyledSubscriptionSelectPage showUserIcons={showUserIcons}>
        <div className="page-header-wrap">
          <div className="page-header-main">
            <h1 className="page-title">Select Your Subscription Plan</h1>
            <CloseButton onClick={() => history.push(ROUTES.user.settings.billing)} />
          </div>
          {showUserIcons && <UserIcons />}
          {subscriptionInfo?.plan?.interval === TRIAL && (
            <div className="discount-alert">
              <InformationCircleIcon />
              <p>
                Receive a discount on your first payment by signing up before your free trial ends.
              </p>
            </div>
          )}
          <h3 className="page-subtitle">Available Plans</h3>
        </div>
        {!loadingSubscription && (
          <div className="subscription-cards">
            {subscriptionInfo?.plan?.interval !== FOREVER ? (
              <>
                {showTrialOption && isReady && isEnabled(trialOption) && (
                  <SubscriptionCard
                    title="Free Trial"
                    plan={trialPriceData}
                    description="Start your 14-day free trial. No credit card required."
                    userSub={subscriptionInfo}
                    initialLength={0}
                    isOpen={cardsOpen}
                    toggleOpen={() => setCardsOpen(!cardsOpen)}
                    features={[
                      'Instant access to the caption library',
                      'Unlimited number of post drafts',
                      'Access to thousands lifestyle images',
                      'Daily bite-sized actions to boost engagement on social media',
                      'Slaying Social: a marketing Action Plan designed to help you skyrocket results on social media',
                      '5 customizable Instagram story sets '
                    ]}
                    setShowCancelModal={setShowModal}
                  />
                )}
                <SubscriptionCard
                  title="Monthly"
                  plan={monthPriceData}
                  interval="month"
                  description="Simple marketing tools to get more followers, more engagement & more sales"
                  userSub={subscriptionInfo}
                  isOwned={subscriptionInfo?.plan?.interval === MONTH}
                  initialLength={3}
                  isOpen={cardsOpen}
                  toggleOpen={() => setCardsOpen(!cardsOpen)}
                  features={[
                    'Instant access to the caption library',
                    'Unlimited number of post drafts',
                    'Access to over 3000 customizable caption templates',
                    'Access to thousands of lifestyle images',
                    'A gallery of 6000+ lifestyle photos',
                    'Access to Social Curator Community live masterclasses & group coaching',
                    'Daily bite-sized actions to boost engagement on social media',
                    'A NEW marketing Action Plan every month',
                    '5 new Instagram story sets every month'
                  ]}
                  setShowCancelModal={setShowModal}
                />
                <SubscriptionCard
                  title="Annual"
                  plan={annualPriceData}
                  interval="year"
                  description={`$${annualSavings} cost savings compared to the monthly plan`}
                  mostPopular
                  userSub={subscriptionInfo}
                  isOwned={subscriptionInfo?.plan?.interval === YEAR}
                  initialLength={5}
                  isOpen={cardsOpen}
                  toggleOpen={() => setCardsOpen(!cardsOpen)}
                  features={[
                    'Priority Customer Support',
                    'Instant access to the caption library',
                    'Access to over 3000 customizable caption templates',
                    'Unlimited number of post drafts',
                    'Access to thousands of lifestyle images',
                    'A gallery of 6000+ lifestyle photos',
                    'Daily bite-sized actions to boost engagement on social media',
                    'Access to over 50 step-by-step marketing Action Plans',
                    'Live masterclasses, group coaching and accountability in the Social Curator Community',
                    'A gallery of over 200 engagement-driven Instagram story set customizable in Canva'
                  ]}
                  setShowCancelModal={setShowModal}
                />
              </>
            ) : (
              <SubscriptionCard
                title="Forever"
                plan={foreverPriceData}
                description="You are subscribed... forever."
                userSub={subscriptionInfo}
                isOwned={subscriptionInfo?.plan.interval === FOREVER}
                isOpen={cardsOpen}
                toggleOpen={() => setCardsOpen(!cardsOpen)}
                setShowCancelModal={setShowModal}
              />
            )}
          </div>
        )}
        <ActionDialog
          type="error"
          isOpen={showModal}
          title="Cancel your subscription?"
          body={(
            <span>
              Once cancelled, you will have {periodEndsInDays} days to continue using Social
              Curator. After that period, you will lose access to the content in your dashboard. You
              will not lose access to any courses that you have purchased. Please feel free to check
              out our{' '}
              <a href={ROUTES.external.cancellationPolicyUrl} target="__blank">
                cancelation policy
              </a>{' '}
              for more information on this process.
            </span>
          )}
          cancelButtonText="Don't Cancel"
          confirmButtonText="End Subscription"
          onClose={() => setShowModal(false)}
          onConfirm={() => handleCancelUserSub()}
        />
      </StyledSubscriptionSelectPage>
    </PageLayout>
  )
}

export default SubscriptionSelectPage
